/**
 * Inquiry Basket Page
 *
 * Replaces BulkOrderPage. Shows items from inquiryStore.
 * Supports mixed baskets (sample + bulk) submitted as separate inquiries.
 * Two modes:
 * - Not authenticated: Full form with phone + inline OTP
 * - Already authenticated: Simplified form, pre-filled user data
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Banner,
  Box,
  Divider,
  Badge,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { OTPInput } from '../components/common/OTPInput';
import type { User } from '@gifting/shared';
import { useInquiryStore } from '../store/inquiryStore';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import * as authService from '../services/auth.service';
import { submitInquiryWithOTP, createInquiry } from '../services/inquiry.service';

type SubmitStep = 'review' | 'phone' | 'otp' | 'details' | 'success';

// OTP is PAUSED until an SMS provider is wired up. When VITE_OTP_ENABLED is
// "false", we skip the send-code / enter-code step and submit straight away so
// the inquiry still lands in admin. Flip it back to "true" once OTP sending
// works — the OTP screen and logic below are kept, just not shown while paused.
const OTP_ENABLED = import.meta.env.VITE_OTP_ENABLED !== 'false';
// A 6-char placeholder the backend ignores while OTP is paused (schema needs 6).
const OTP_PLACEHOLDER = '000000';

export function InquiryBasketPage() {
  const navigate = useNavigate();
  const { items, updateItem, removeItem, clearCart, clearItemsByType } = useInquiryStore();
  const { isAuthenticated, user, login: setAuth } = useAuthStore();
  const { showToast } = useUIStore();

  const [step, setStep] = useState<SubmitStep>('review');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState(user?.name || '');
  const [companyName, setCompanyName] = useState(user?.companyName || '');
  const [city, setCity] = useState((user as { city?: string })?.city || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceNos, setReferenceNos] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Partition items by type
  const sampleItems = items.filter((i) => i.type === 'SAMPLE');
  const bulkItems = items.filter((i) => i.type === 'BULK');
  const isMixed = sampleItems.length > 0 && bulkItems.length > 0;

  // Handle OTP send
  const handleSendOTP = useCallback(async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authService.requestOTP(phone, 'phone');
      showToast('OTP sent to your phone', 'success');
      setStep('otp');
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [phone, showToast]);

  // Map cart items to API format
  const toApiItems = (cartItems: typeof items) =>
    cartItems.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      color: item.color || undefined,
      notes: item.notes || undefined,
    }));

  // Handle submit for unauthenticated users (inline OTP flow)
  const handleSubmitWithOTP = useCallback(async (otp: string) => {
    if (!name.trim() || !city.trim()) {
      setStep('details');
      setError('Please fill in the required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    const refs: string[] = [];

    try {
      // Determine what to submit first
      const hasBulk = bulkItems.length > 0;
      const hasSample = sampleItems.length > 0;
      const firstType = hasBulk ? 'BULK_ORDER' : 'SAMPLE_REQUEST';
      const firstItems = hasBulk ? bulkItems : sampleItems;

      // First call: submit with OTP (consumes OTP, returns tokens)
      const result = await submitInquiryWithOTP({
        phone,
        otp,
        name,
        companyName: companyName || undefined,
        city,
        items: toApiItems(firstItems),
        type: firstType,
        notes: notes || undefined,
      });

      // Store auth tokens from the response
      setAuth(result.user as unknown as User, result.tokens);
      refs.push(result.inquiry.referenceNo);
      clearItemsByType(hasBulk ? 'BULK' : 'SAMPLE');

      // Second call (if mixed): use Bearer token from first response
      if (hasBulk && hasSample) {
        try {
          const secondResult = await createInquiry({
            type: 'SAMPLE_REQUEST',
            items: toApiItems(sampleItems),
            deliveryAddress: city || 'To be confirmed',
            notes: notes || undefined,
          });
          refs.push(secondResult.referenceNo);
          clearItemsByType('SAMPLE');
        } catch {
          // Partial failure: bulk succeeded, sample failed
          setReferenceNos(refs);
          setError(`Bulk inquiry submitted (${refs[0]}) but sample request failed. Your sample items are still in the basket — please try again.`);
          setSubmitting(false);
          return;
        }
      }

      setReferenceNos(refs);
      clearCart();
      setStep('success');
      showToast('Inquiry submitted successfully!', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      if (message.includes('OTP') || message.includes('otp')) {
        setError('OTP expired or invalid. Please request a new one.');
        setStep('phone');
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }, [phone, name, companyName, city, notes, bulkItems, sampleItems, setAuth, clearCart, clearItemsByType, showToast]);

  // What the main submit button does.
  // OTP on  → send the code, then show the OTP screen (normal flow).
  // OTP off → skip straight to submitting with a placeholder code.
  const handlePrimaryAction = useCallback(() => {
    if (OTP_ENABLED) return handleSendOTP();
    return handleSubmitWithOTP(OTP_PLACEHOLDER);
  }, [handleSendOTP, handleSubmitWithOTP]);

  // Handle submit for authenticated users
  const handleAuthenticatedSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    const refs: string[] = [];
    const hasBulk = bulkItems.length > 0;
    const hasSample = sampleItems.length > 0;

    try {
      // Submit bulk items
      if (hasBulk) {
        const result = await createInquiry({
          type: 'BULK_ORDER',
          items: toApiItems(bulkItems),
          deliveryAddress: city || 'To be confirmed',
          notes: notes || undefined,
        });
        refs.push(result.referenceNo);
        clearItemsByType('BULK');
      }

      // Submit sample items
      if (hasSample) {
        try {
          const result = await createInquiry({
            type: 'SAMPLE_REQUEST',
            items: toApiItems(sampleItems),
            deliveryAddress: city || 'To be confirmed',
            notes: notes || undefined,
          });
          refs.push(result.referenceNo);
          clearItemsByType('SAMPLE');
        } catch {
          if (hasBulk && refs.length > 0) {
            // Partial failure: bulk succeeded, sample failed
            setReferenceNos(refs);
            setError(`Bulk inquiry submitted (${refs[0]}) but sample request failed. Your sample items are still in the basket — please try again.`);
            setSubmitting(false);
            return;
          }
          throw new Error('Failed to submit sample request');
        }
      }

      setReferenceNos(refs);
      clearCart();
      setStep('success');
      showToast('Inquiry submitted successfully!', 'success');
    } catch {
      setError('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [bulkItems, sampleItems, city, notes, clearCart, clearItemsByType, showToast]);

  // Empty basket
  if (items.length === 0 && step !== 'success') {
    return (
      <Page title="Your Inquiry Basket" narrowWidth>
        <Card>
          <BlockStack gap="400" inlineAlign="center">
            <Text as="h2" variant="headingMd">Your basket is empty</Text>
            <Text as="p" tone="subdued">
              Browse our products and add items to get a quote.
            </Text>
            <Button variant="primary" onClick={() => navigate('/categories')}>
              Browse Products
            </Button>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  // Success screen
  if (step === 'success') {
    return (
      <Page title="Inquiry Submitted!" narrowWidth>
        <Card>
          <BlockStack gap="400" inlineAlign="center">
            <Text as="h2" variant="headingLg">Thank You!</Text>
            <Text as="p" variant="bodyMd">
              Your {referenceNos.length > 1 ? 'inquiries have' : 'inquiry has'} been submitted successfully.
            </Text>
            {referenceNos.map((ref) => (
              <Box key={ref} padding="400" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100" inlineAlign="center">
                  <Text as="p" variant="bodySm" tone="subdued">Reference Number</Text>
                  <Text as="p" variant="headingLg" fontWeight="bold">{ref}</Text>
                </BlockStack>
              </Box>
            ))}
            <Text as="p" variant="bodySm" tone="subdued">
              Our team will review your {referenceNos.length > 1 ? 'inquiries' : 'inquiry'} and get back to you within 24-48 hours.
            </Text>
            <InlineStack gap="300">
              <Button variant="primary" onClick={() => navigate('/inquiries')}>
                Track Your Inquiries
              </Button>
              <Button onClick={() => navigate('/categories')}>
                Continue Browsing
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Your Inquiry Basket"
      subtitle={`${items.length} item${items.length !== 1 ? 's' : ''}`}
    >
      <Layout>
        {/* Items Review */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Items</Text>

              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}

              {isMixed && (
                <Banner tone="info">
                  {bulkItems.length} bulk item{bulkItems.length !== 1 ? 's' : ''} + {sampleItems.length} sample{sampleItems.length !== 1 ? 's' : ''} — will be submitted as 2 separate inquiries.
                </Banner>
              )}

              {items.map((item) => (
                <div key={`${item.productId}-${item.color}-${item.type}`}>
                  <InlineStack align="space-between" blockAlign="center" gap="400">
                    <InlineStack gap="300" blockAlign="center">
                      {item.productImage ? (
                        <img
                          src={item.productImage}
                          alt={item.productName}
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <Box minWidth="48px" minHeight="48px" background="bg-surface-secondary" borderRadius="100" />
                      )}
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">{item.productName}</Text>
                          <Badge tone={item.type === 'SAMPLE' ? 'attention' : 'info'}>
                            {item.type === 'SAMPLE' ? 'Sample' : 'Bulk'}
                          </Badge>
                        </InlineStack>
                        {item.color && <Badge>{item.color}</Badge>}
                      </BlockStack>
                    </InlineStack>
                    <InlineStack gap="300" blockAlign="center">
                      {item.type === 'SAMPLE' ? (
                        <Text as="p" variant="bodySm" tone="subdued">Qty: 1</Text>
                      ) : (
                        <TextField
                          label=""
                          labelHidden
                          type="number"
                          value={String(item.quantity)}
                          onChange={(val) => updateItem(item.productId, item.type, { quantity: parseInt(val) || 1 })}
                          min={1}
                          autoComplete="off"
                          connectedLeft={<Text as="span" variant="bodySm">Qty:</Text>}
                        />
                      )}
                      <Button
                        icon={DeleteIcon}
                        variant="plain"
                        tone="critical"
                        onClick={() => removeItem(item.productId, item.type)}
                        accessibilityLabel="Remove item"
                      />
                    </InlineStack>
                  </InlineStack>
                  <Box paddingBlockStart="300">
                    <Divider />
                  </Box>
                </div>
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Submission Form */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Submit Inquiry</Text>

              {isAuthenticated ? (
                /* Authenticated flow: simplified form */
                <BlockStack gap="300">
                  <TextField
                    label="Notes (optional)"
                    value={notes}
                    onChange={setNotes}
                    multiline={3}
                    placeholder="Any special requirements..."
                    autoComplete="off"
                  />
                  <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleAuthenticatedSubmit}
                    loading={submitting}
                    disabled={submitting}
                  >
                    Submit Inquiry
                  </Button>
                </BlockStack>
              ) : (
                /* Unauthenticated flow: phone → OTP → details → submit */
                <BlockStack gap="300">
                  {(step === 'review' || step === 'phone') && (
                    <>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Confirm your number so we can call you back
                      </Text>
                      <TextField
                        label="Phone Number"
                        type="tel"
                        value={phone}
                        onChange={setPhone}
                        placeholder="+91 98765 43210"
                        autoComplete="tel"
                      />
                      <TextField
                        label="Your Name"
                        value={name}
                        onChange={setName}
                        placeholder="Enter your name"
                        autoComplete="name"
                        requiredIndicator
                      />
                      <TextField
                        label="Company (optional)"
                        value={companyName}
                        onChange={setCompanyName}
                        placeholder="Your company name"
                        autoComplete="organization"
                      />
                      <TextField
                        label="City"
                        value={city}
                        onChange={setCity}
                        placeholder="Your city"
                        autoComplete="address-level2"
                        requiredIndicator
                      />
                      <TextField
                        label="Notes (optional)"
                        value={notes}
                        onChange={setNotes}
                        multiline={3}
                        placeholder="Any special requirements..."
                        autoComplete="off"
                      />
                      <Button
                        variant="primary"
                        size="large"
                        fullWidth
                        onClick={handlePrimaryAction}
                        loading={loading || submitting}
                        disabled={!phone.trim() || !name.trim() || !city.trim()}
                      >
                        {OTP_ENABLED ? 'Send OTP & Submit' : 'Submit Inquiry'}
                      </Button>
                    </>
                  )}

                  {step === 'otp' && (
                    <>
                      <Text as="p" variant="bodyMd">
                        We sent a 6-digit code to <Text as="span" fontWeight="semibold">{phone}</Text>
                      </Text>
                      <OTPInput
                        onComplete={handleSubmitWithOTP}
                        loading={submitting}
                      />
                      <Button
                        variant="plain"
                        onClick={() => {
                          setStep('phone');
                          setError(null);
                        }}
                      >
                        Change phone number
                      </Button>
                    </>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
